/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AiChatbotFormView.tsx
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the AI Integration module.
────────────────────────────────────────────────────────────────*/


import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MessageSquare, User, FileText, Brain, Plus, Eye, Upload } from 'lucide-react';

import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { api } from '../../lib/api';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Toggle } from '../../components/ui/Toggle';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { Label } from '../../components/ui/label';
import { FormBox } from '../../components/ui/FormBox';
import ModuleDestinationSelect from '../../components/ui/ModuleDestinationSelect';
import ConfirmationModal from '../../components/ui/ConfirmationModal';

import { getAiAgentById, createAiAgent, updateAiAgent, deleteAiAgent } from '../ai-agents/api/aiAgentsService';
import { getAiProfiles, type AiProfile } from '../ai-profiles/api/aiProfilesApi';
import PromptPreviewModal from '../ai-agents/components/PromptPreviewModal';
import DynamicContextsTab from '../ai-agents/components/DynamicContextsTab';
import PersonalityTab from '../ai-agents/components/PersonalityTab';
import { AiAgentContextTab } from '../ai-agents/components/AiAgentContextTab';
import { generateChatbotPromptFromFormData } from './utils/promptGeneratorChatbot';
import { listToolProfilesQuickList } from '../ai-tool-profiles/api/aiToolProfilesApi';
import { SearchableSelect } from '../../components/ui/SearchableSelect';



interface TransferRoute {
  name: string;
  destination_type: string;
  destination: string;
}

interface AiChatbotFormData {
  agent_name: string; description: string;
  provider_id: number | null; ai_profile_id: string | null; embedding_profile_id: string | null; prompt_generator_profile_id: string | null;
  tool_profile_id: string | null;

  voice: string; conversation_mode: string; language_mode: string; forced_language: string;
  barge_in_enabled: boolean; barge_in_sensitivity: number; barge_in_min_speech_ms: number;
  greeting_protection_ms: number; greeting_protection: boolean;
  idle_timeout_ms: number; idle_retries: number; audio_drain_ms: number;
  transcription_enabled: boolean; transcription_profile_id: string | null;
  integration_ids: string[];
  store_transcripts: boolean; live_transcript: boolean; record_call: boolean;
  translation_enabled: boolean; translation_target_language: string;
  disclosure_enabled: boolean; disclosure_message: string; pii_redaction_enabled: boolean;
  max_duration_seconds: number; conversation_start: string;
  system_prompt: string; greeting_message: string;
  persona_name: string; role_description: string; personality: string;
  tone_of_voice: string; language_level: string; speaking_style: string;
  response_length: string; filler_words: boolean; reduce_repetition: boolean;
  allow_language_change: boolean; company_name: string; products: string[];
  key_points: string; team_members: Record<string, string>;
  do_rules: string[]; dont_rules: string[];
  pronunciations: Record<string, string>;
  closing_message: string; additional_instructions: string;
  enable_hangup: boolean; enable_transfer: boolean; open_transfer: boolean;
  transfer_routes: TransferRoute[];
  timeout_destination_type: string; timeout_destination: string;
  failure_destination_type: string; failure_destination: string;
  prompt_outdated: boolean; enabled: boolean; is_default_chatbot: boolean;
}

const defaultFormData: AiChatbotFormData = {
  agent_name: '', description: '',
  provider_id: null, ai_profile_id: null, embedding_profile_id: null, prompt_generator_profile_id: null,
  tool_profile_id: null,

  voice: 'alloy', conversation_mode: 'natural', language_mode: 'auto', forced_language: '',
  barge_in_enabled: true, barge_in_sensitivity: 0.8, barge_in_min_speech_ms: 280,
  greeting_protection_ms: 5000, greeting_protection: true,
  idle_timeout_ms: 15000, idle_retries: 2, audio_drain_ms: 3000,
  transcription_enabled: true, transcription_profile_id: null,
  integration_ids: [],
  store_transcripts: true, live_transcript: true, record_call: false,
  translation_enabled: false, translation_target_language: '',
  disclosure_enabled: true, disclosure_message: 'You are now speaking with an AI assistant.',
  pii_redaction_enabled: false, max_duration_seconds: 300, conversation_start: 'agent',
  system_prompt: '', greeting_message: 'Hello! How can I help you today?',
  persona_name: '', role_description: '', personality: 'friendly',
  tone_of_voice: 'warm', language_level: 'conversational', speaking_style: 'natural',
  response_length: 'standard', filler_words: false, reduce_repetition: true,
  allow_language_change: false, company_name: '', products: [],
  key_points: '', team_members: {}, do_rules: [], dont_rules: [],
  pronunciations: {}, closing_message: '', additional_instructions: '',
  enable_hangup: true, enable_transfer: false, open_transfer: false,
  transfer_routes: [],
  timeout_destination_type: '', timeout_destination: '',
  failure_destination_type: '', failure_destination: '',
  prompt_outdated: false, enabled: true, is_default_chatbot: false,
};

type TabType = 'general' | 'personality' | 'context' | 'instructions' | 'advanced';


export default function AiChatbotFormView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  const isNew = !id || id === 'new';
  const isEditMode = !isNew;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<AiChatbotFormData>(defaultFormData);
  const [originalFormData, setOriginalFormData] = useState<AiChatbotFormData>(defaultFormData);
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [aiProfiles, setAiProfiles] = useState<AiProfile[]>([]);
  const [availableIntegrations, setAvailableIntegrations] = useState<{ id: string; name: string; provider: string }[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [toolProfiles, setToolProfiles] = useState<{ id: number; name: string }[]>([]);
  const [formStateAtLastGeneration, setFormStateAtLastGeneration] = useState<string>('');

  const [dynamicContextRefreshKey, setDynamicContextRefreshKey] = useState(0);
  const [showIngestModal, setShowIngestModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleIngestSystemManualsClick = () => {
    if (!id || id === 'new') return;
    if (!formData.ai_profile_id) {
        showError('Please select an AI Profile first (General Tab)');
        return;
    }
    setShowIngestModal(true);
  };

  const confirmIngestSystemManuals = async () => {
    setShowIngestModal(false);
    
    try {
        setSaving(true);
        const resp = await api.post(`/api/telephony/ai-agents/${id}/dynamic-contexts/ingest-system-manual`, {
            aiProfileId: formData.ai_profile_id
        });
        showSuccess(`Ingestion started! Enqueued ${resp.data?.enqueued || 0} documents for processing.`);
        setDynamicContextRefreshKey(prev => prev + 1);
    } catch (err: any) {
        showError(err?.response?.data?.error || 'Failed to ingest system manuals');
    } finally {
        setSaving(false);
    }
  };


  useEffect(() => {
    const fetchProfiles = async () => {
      setLoadingProfiles(true);
      try {
        const [general] = await Promise.all([
          getAiProfiles({ page: 1, limit: 100, sortBy: 'profile_name', sortOrder: 'asc', type: 'general' }),
        ]);
        setAiProfiles(general.data || []);
        try {
          const intResp = await api.get('/api/integrations/crm/connections');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const filtered = (intResp.data || []).filter((i: any) => i.settings?.ai_enabled || ['CALENDLY', 'ACUITY', 'SQUARE_APPOINTMENTS'].includes(i.provider?.toUpperCase()));
          setAvailableIntegrations(filtered);
        } catch { /* ignore */ }
        try { const tp = await listToolProfilesQuickList(); setToolProfiles(tp); } catch { /* ignore */ }
      } catch (err) { console.error('Failed to load AI profiles:', err); }

      finally { setLoadingProfiles(false); }
    };
    fetchProfiles();
  }, []);

  useEffect(() => {
    if (id && id !== 'new') { setLoading(true); fetchAgent(id); }
  }, [id]);

  const fetchAgent = async (agentId: number | string) => {
    try {
      const agent = await getAiAgentById(agentId);
      const loaded: AiChatbotFormData = {
        agent_name: agent.agentName || '', description: agent.description || '',
        provider_id: null,
        ai_profile_id: agent.aiProfileId ? String(agent.aiProfileId) : null,
        embedding_profile_id: (agent as any).embeddingProfileId ? String((agent as any).embeddingProfileId) : null,
        prompt_generator_profile_id: agent.promptGeneratorProfileId ? String(agent.promptGeneratorProfileId) : null,
        tool_profile_id: (agent as any).toolProfileId ? String((agent as any).toolProfileId) : null,

        voice: agent.voice || 'alloy', conversation_mode: agent.conversationMode || 'natural',
        language_mode: agent.languageMode || 'auto', forced_language: agent.forcedLanguage || '',
        barge_in_enabled: agent.bargeInEnabled ?? true, barge_in_sensitivity: agent.bargeInSensitivity ?? 0.8,
        barge_in_min_speech_ms: agent.bargeInMinSpeechMs ?? 280, greeting_protection_ms: agent.greetingProtectionMs ?? 5000,
        greeting_protection: agent.greetingProtection ?? true, idle_timeout_ms: agent.idleTimeoutMs ?? 15000,
        idle_retries: agent.idleRetries ?? 2, audio_drain_ms: agent.audioDrainMs ?? 3000,
        transcription_enabled: agent.transcriptionEnabled ?? true, transcription_profile_id: agent.transcriptionProfileId ? String(agent.transcriptionProfileId) : null,
        integration_ids: agent.integrationIds || [],
        store_transcripts: agent.storeTranscripts ?? true, live_transcript: agent.liveTranscript ?? true,
        record_call: agent.recordCall ?? false, translation_enabled: agent.translationEnabled ?? false,
        translation_target_language: agent.translationTargetLanguage || '',
        disclosure_enabled: agent.disclosureEnabled ?? true, disclosure_message: agent.disclosureMessage || 'You are now speaking with an AI assistant.',
        pii_redaction_enabled: agent.piiRedactionEnabled ?? false, max_duration_seconds: agent.maxDurationSeconds ?? 300,
        conversation_start: agent.conversationStart || 'agent', system_prompt: agent.systemPrompt || '',
        greeting_message: agent.greetingMessage || '',
        persona_name: agent.personaName || '', role_description: agent.roleDescription || '',
        personality: agent.personality || 'friendly', tone_of_voice: agent.toneOfVoice || 'warm',
        language_level: agent.languageLevel || 'conversational', speaking_style: agent.speakingStyle || 'natural',
        response_length: agent.responseLength || 'standard', filler_words: agent.fillerWords ?? false,
        reduce_repetition: agent.reduceRepetition ?? true, allow_language_change: agent.allowLanguageChange ?? false,
        company_name: agent.companyName || '', products: agent.products || [],
        key_points: agent.keyPoints || '', team_members: agent.teamMembers || {},
        do_rules: agent.doRules || [], dont_rules: agent.dontRules || [],
        pronunciations: agent.pronunciations || {}, closing_message: agent.closingMessage || '',
        additional_instructions: agent.additionalInstructions || '',
        enable_hangup: agent.enableHangup ?? true, enable_transfer: agent.enableTransfer ?? false,
        open_transfer: agent.openTransfer ?? false, transfer_routes: agent.transferRoutes || [],
        timeout_destination_type: agent.timeoutDestinationType || '', timeout_destination: agent.timeoutDestination || '',
        failure_destination_type: agent.failureDestinationType || '', failure_destination: agent.failureDestination || '',
        prompt_outdated: agent.promptOutdated ?? false, enabled: agent.enabled ?? true, is_default_chatbot: agent.isDefaultChatbot ?? false,
      };
      setFormData(loaded);
      setOriginalFormData(loaded);
      if (agent.systemPrompt?.trim()) {
        setFormStateAtLastGeneration(JSON.stringify({ persona_name: loaded.persona_name, role_description: loaded.role_description, personality: loaded.personality, company_name: loaded.company_name, products: loaded.products, key_points: loaded.key_points, team_members: loaded.team_members, do_rules: loaded.do_rules, dont_rules: loaded.dont_rules, pronunciations: loaded.pronunciations, greeting_message: loaded.greeting_message, closing_message: loaded.closing_message, disclosure_message: loaded.disclosure_message, additional_instructions: loaded.additional_instructions }));
      }
    } catch {
      showError(t('aiAgents.messages.loadError') || 'Failed to load agent');
      navigate('/admin/ai-integration/chatbots');
    } finally { setLoading(false); }
  };

  const promptRelevantFields = new Set(['persona_name', 'role_description', 'personality', 'company_name', 'products', 'key_points', 'team_members', 'do_rules', 'dont_rules', 'pronunciations', 'greeting_message', 'closing_message', 'disclosure_message', 'additional_instructions']);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = useCallback((field: keyof AiChatbotFormData, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (promptRelevantFields.has(field) && prev.system_prompt?.trim()) next.prompt_outdated = true;
      return next;
    });
  }, []);

  const getPromptHash = (data: AiChatbotFormData) => JSON.stringify({ persona_name: data.persona_name, role_description: data.role_description, personality: data.personality, company_name: data.company_name, products: data.products, key_points: data.key_points, team_members: data.team_members, do_rules: data.do_rules, dont_rules: data.dont_rules, pronunciations: data.pronunciations, greeting_message: data.greeting_message, closing_message: data.closing_message, disclosure_message: data.disclosure_message, additional_instructions: data.additional_instructions });

  const handleSave = async () => {
    const generatedPrompt = generateChatbotPromptFromFormData(formData as any);
    const dataToSave = { ...formData, system_prompt: generatedPrompt, prompt_outdated: false, agentType: 'chat' };
    setFormData(prev => ({ ...prev, system_prompt: generatedPrompt, prompt_outdated: false }));
    setSaving(true);
    try {
      if (isNew) {
        const result = await createAiAgent(dataToSave);
        showSuccess(t('aiAgents.messages.createSuccess') || 'Agent created');
        setOriginalFormData(dataToSave); setFormStateAtLastGeneration(getPromptHash(dataToSave));
        return result;
      } else {
        const result = await updateAiAgent(id!, dataToSave);
        showSuccess(t('aiAgents.messages.updateSuccess') || 'Agent updated');
        setOriginalFormData(dataToSave); setFormStateAtLastGeneration(getPromptHash(dataToSave));
        return result;
      }
    } catch (error) {
      showError((error as Error)?.message || t('aiAgents.messages.saveError') || 'Save failed');
      return undefined;
    } finally { setSaving(false); }
  };

  const handleSaveWithAction = async (action: 'save' | 'save-and-close' | 'save-and-new') => {
    const result = await handleSave();
    if (!result) return;
    if (action === 'save' && isNew && (result.uuid || result.id)) navigate(`/admin/ai-integration/chatbots/${result.uuid ?? result.id}`, { replace: true });
    else if (action === 'save-and-close') navigate('/admin/ai-integration/chatbots');
    else if (action === 'save-and-new') navigate('/admin/ai-integration/chatbots/new');
  };

  const handleDeleteClick = () => setShowDeleteModal(true);

  const handleDeleteConfirm = async () => {
    if (!id || id === 'new') return;
    setIsDeleting(true);
    try {
      await deleteAiAgent(id);
      showSuccess(t('aiAgents.messages.deleteSuccess') || 'Chatbot deleted successfully');
      navigate('/admin/ai-integration/chatbots');
    } catch {
      showError(t('aiAgents.messages.deleteError') || 'Failed to delete chatbot');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;

  return (
    <div className="p-6 pb-20">
      <ModuleFormHeader
        icon={<MessageSquare className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('aiAgents.form.editChatbotTitle') || 'Edit Web Chatbot'} - ${formData.agent_name || ''}` : (t('aiAgents.form.createChatbotTitle') || 'Create Web Chatbot')}
        backButtonText={t('aiAgents.actions.list') || 'List'}
        onBack={() => navigate('/admin/ai-integration/chatbots')}
      >
        <SimpleTooltip content={t('aiAgents.actions.previewPrompt') || 'Preview Prompt'} position="bottom">
          <button type="button" onClick={() => setShowPromptPreview(true)} className="p-2 rounded-lg transition-colors border bg-accent hover:bg-accent/80 border-border text-foreground">
            <Eye className="h-4 w-4" />
          </button>
        </SimpleTooltip>
      </ModuleFormHeader>

      <div className="mt-12">
        <Tabs value={activeTab} onValueChange={(val) => setActiveTab(val as TabType)}>
          <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1">
            <TabsTrigger value="general">{t('aiAgents.tabs.general') || 'General'}</TabsTrigger>
            <TabsTrigger value="personality">{t('aiAgents.tabs.personality') || 'Personality & Tone'}</TabsTrigger>
            <TabsTrigger value="context">{t('aiAgents.tabs.context') || 'Context'}</TabsTrigger>
            <TabsTrigger value="instructions">{t('aiAgents.tabs.instructions') || 'Instructions'}</TabsTrigger>
            {formData.embedding_profile_id && (
              <TabsTrigger value="advanced">{t('aiAgents.tabs.dynamicContext') || 'Dynamic Context'}</TabsTrigger>
            )}

          </TabsList>

          {/* General Tab */}
          <TabsContent value="general">
            <FormBox title={t('aiAgents.tabs.general') || 'General'} icon={<User className="h-5 w-5" />} className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">

                <SimpleTooltip content={t('aiAgents.form.agentNameTooltip')} position="top">
                  <Label required className="text-left">{t('aiAgents.form.agentName') || 'Agent Name'}</Label>
                </SimpleTooltip>
                <input type="text" required value={formData.agent_name} onChange={e => handleChange('agent_name', e.target.value)}
                  className="px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  maxLength={100} placeholder={t('aiAgents.form.agentNamePlaceholder') || 'Enter agent name'} />

                <SimpleTooltip content={t('aiAgents.form.descriptionTooltip')} position="top">
                  <Label className="text-left">{t('aiAgents.form.description') || 'Description'}</Label>
                </SimpleTooltip>
                <input type="text" value={formData.description} onChange={e => handleChange('description', e.target.value)}
                  className="px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  maxLength={500} placeholder={t('aiAgents.form.descriptionPlaceholder') || 'Brief description'} />

                <SimpleTooltip content={t('aiAgents.form.aiProfileTooltip')} position="top">
                  <Label required className="text-left">{t('aiAgents.form.aiProfile') || 'AI Profile'}</Label>
                </SimpleTooltip>
                <SearchableSelect
                  id="agent-ai-profile"
                  value={formData.ai_profile_id}
                  onChange={v => handleChange('ai_profile_id', v)}
                  loading={loadingProfiles}
                  placeholder={t('aiAgents.form.selectProfile') || 'Select AI Profile'}
                  searchPlaceholder="Search AI profiles…"
                  options={aiProfiles.map(p => ({ value: String(p.id), label: p.profile_name, subLabel: p.model }))}
                />

                <SimpleTooltip content="Select an AI Profile to generate embeddings for dynamic context." position="top">
                  <Label className="text-left">Embedding Profile</Label>
                </SimpleTooltip>
                <SearchableSelect
                  id="agent-embedding-profile"
                  value={formData.embedding_profile_id}
                  onChange={v => handleChange('embedding_profile_id', v)}
                  loading={loadingProfiles}
                  placeholder="Select Embedding Profile"
                  searchPlaceholder="Search AI profiles…"
                  options={aiProfiles.map(p => ({ value: String(p.id), label: p.profile_name, subLabel: p.model }))}
                />

                <SimpleTooltip content="Assign a Tool Profile to give this agent access to external integrations (Calendly, HubSpot, etc.)" position="top">
                  <Label className="text-left">Tool Profile</Label>
                </SimpleTooltip>
                <SearchableSelect
                  id="agent-tool-profile"
                  value={formData.tool_profile_id}
                  onChange={v => handleChange('tool_profile_id', v)}
                  placeholder="— None (no tools) —"
                  searchPlaceholder="Search tool profiles…"
                  options={toolProfiles.map(p => ({ value: String(p.id), label: p.name }))}
                />

                <SimpleTooltip content={t('aiAgents.form.integrationsTooltip') || 'Select integrations (CRM, APIs) that this AI agent can use as tools during conversations'} position="top">
                  <Label className="text-left">{t('aiAgents.form.integrations') || 'Integration'}</Label>
                </SimpleTooltip>
                <SearchableSelect
                  id="agent-integration"
                  value={formData.integration_ids[0] || null}
                  onChange={v => handleChange('integration_ids', v ? [v] : [])}
                  placeholder="-- No Integration --"
                  searchPlaceholder="Search integrations…"
                  options={availableIntegrations.map(i => ({ value: i.id, label: i.name, subLabel: i.provider }))}
                />
              </div>

              <div className="mt-6 pt-6 border-t border-border grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                <SimpleTooltip content={t('aiAgents.form.enabledTooltip')} position="top">
                  <Label className="text-left">{t('common.enabled') || 'Enabled'}</Label>
                </SimpleTooltip>
                <Toggle value={formData.enabled} onChange={(v) => handleChange('enabled', v)} />

                <SimpleTooltip content={t('aiAgents.form.isDefaultChatbotTooltip') || 'Set this AI Agent as the default Chatbot for the system.'} position="top">
                  <Label className="text-left">{t('aiAgents.form.isDefaultChatbot') || 'Use for Chatbot'}</Label>
                </SimpleTooltip>
                <div className="flex items-start">
                  <Toggle value={formData.is_default_chatbot} onChange={(v) => handleChange('is_default_chatbot', v)} />
                </div>
              </div>
            </FormBox>
          </TabsContent>

          {/* Personality Tab */}
          <TabsContent value="personality">
            <FormBox title={t('aiAgents.tabs.personality') || 'Personality & Tone'} icon={<Brain className="h-5 w-5" />} className="mt-6">
              <PersonalityTab
                agentType="chat"
                formData={{
                  personality: formData.personality,
                  tone_of_voice: formData.tone_of_voice,
                  language_level: formData.language_level,
                  response_length: formData.response_length,
                  reduce_repetition: formData.reduce_repetition,
                }}
                onChange={(field, value) => handleChange(field as keyof AiChatbotFormData, value)}
              />
            </FormBox>
          </TabsContent>

          {/* Context Tab */}
          <TabsContent value="context">
            <AiAgentContextTab
              showSection="context"
              agentType="chat"
              formData={{ company_name: formData.company_name, products: formData.products, key_points: formData.key_points, disclosure_enabled: formData.disclosure_enabled, pii_redaction_enabled: formData.pii_redaction_enabled, disclosure_message: formData.disclosure_message, persona_name: formData.persona_name, role_description: formData.role_description, greeting_message: formData.greeting_message, do_rules: formData.do_rules, dont_rules: formData.dont_rules, closing_message: formData.closing_message }}
              onChange={(field, value) => handleChange(field as keyof AiChatbotFormData, value)} />
          </TabsContent>

          {/* Instructions Tab */}
          <TabsContent value="instructions">
            <AiAgentContextTab
              showSection="instructions"
              agentType="chat"
              formData={{ company_name: formData.company_name, products: formData.products, key_points: formData.key_points, disclosure_enabled: formData.disclosure_enabled, pii_redaction_enabled: formData.pii_redaction_enabled, disclosure_message: formData.disclosure_message, persona_name: formData.persona_name, role_description: formData.role_description, greeting_message: formData.greeting_message, do_rules: formData.do_rules, dont_rules: formData.dont_rules, closing_message: formData.closing_message }}
              onChange={(field, value) => handleChange(field as keyof AiChatbotFormData, value)} />
          </TabsContent>

          {/* Dynamic Context Tab */}
          <TabsContent value="advanced">
            <FormBox title={t('aiAgents.tabs.dynamicContext') || 'Dynamic Context'} icon={<FileText className="h-5 w-5" />} className="mt-6"
              headerContent={!isNew && (
                <div className="flex items-center gap-2">
                  <button type="button" onClick={handleIngestSystemManualsClick} disabled={saving}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors">
                    <Plus className="h-4 w-4" />Ingest System Manuals
                  </button>

                  <button type="button" onClick={() => document.getElementById('dynamic-context-file-input')?.click()}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-md transition-colors">
                    <Upload className="h-4 w-4" />{t('aiAgents.dynamicContext.uploadDocument') || 'Upload Document'}
                  </button>

                  <button type="button" onClick={() => { window.dispatchEvent(new CustomEvent('openAddContextModal')); }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-colors">
                    <Plus className="h-4 w-4" />{t('aiAgents.dynamicContext.addManual') || 'Add Manual'}
                  </button>
                </div>
              )}>
              <DynamicContextsTab key={dynamicContextRefreshKey} agentId={id && id !== 'new' ? id : null} isNew={isNew} aiProfileId={formData.prompt_generator_profile_id as string | null} isDefaultChatbot={formData.is_default_chatbot} />
            </FormBox>
          </TabsContent>
        </Tabs>


      </div>

      <FixedActionBar onSaveWithAction={handleSaveWithAction} onCancel={() => setFormData({ ...originalFormData })}
        onDelete={isEditMode ? handleDeleteClick : undefined}
        isEditMode={isEditMode} isSaving={saving} cancelText={t('common.cancel') || 'Cancel'}
        deleteText={t('common.delete') || 'Delete'} deleteDisabled={isDeleting}
        useSaveDropdown={true} saveDropdownStorageKey="aiAgents:saveAction"
        saveDisabled={saving || !formData.agent_name.trim()} />

      <PromptPreviewModal isOpen={showPromptPreview} onClose={() => setShowPromptPreview(false)} formData={formData}
        generatorFn={generateChatbotPromptFromFormData}
        onApplyGeneratedPrompt={(prompt) => {
          setFormData(prev => ({ ...prev, system_prompt: prompt, prompt_outdated: false }));
          setFormStateAtLastGeneration(getPromptHash(formData));
        }} />

      <ConfirmationModal
        isOpen={showIngestModal}
        onClose={() => setShowIngestModal(false)}
        onConfirm={confirmIngestSystemManuals}
        title="Ingest System Manuals"
        message="Are you sure you want to ingest the system manuals? This will add all distribution markdown files as Knowledge Source for this chatbot."
        confirmText="Ingest"
        cancelText="Cancel"
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('common.deleteItem') || 'Delete Chatbot'}
        message={`${t('common.areYouSureDeleteItem') || 'Are you sure you want to delete'} "${formData.agent_name}"?`}
        confirmText={t('common.delete') || 'Delete'}
        cancelText={t('common.cancel') || 'Cancel'}
        isLoading={isDeleting}
        type="danger"
      />

    </div>
  );
}
